import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const QUICK_OPTIONS = [
  { id: "too_technical", label: "Too technical" },
  { id: "too_simple", label: "Too simple" },
  { id: "vague", label: "Vague" },
  { id: "was_useful", label: "Was useful" },
];

interface SessionFeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  pagePath?: string;
  courseId?: string;
  chapterId?: string;
}

export function SessionFeedbackModal({
  open,
  onOpenChange,
  sessionId,
  pagePath,
  courseId,
  chapterId,
}: SessionFeedbackModalProps) {
  const { toast } = useToast();
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [textFeedback, setTextFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleOption = (id: string) => {
    setSelectedOptions((prev) =>
      prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (rating === 0 && selectedOptions.length === 0 && !textFeedback.trim()) {
      toast({ title: "Please provide some feedback", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("session_feedback").insert({
        session_id: sessionId,
        rating: rating > 0 ? rating : null,
        quick_feedback: selectedOptions,
        text_feedback: textFeedback.trim() || null,
        page_path: pagePath || null,
        course_id: courseId || null,
        chapter_id: chapterId || null,
      });

      if (error) throw error;

      toast({ title: "Thanks for your feedback!" });
      onOpenChange(false);
      // Reset state
      setRating(0);
      setSelectedOptions([]);
      setTextFeedback("");
    } catch (err) {
      console.error("Feedback submission error:", err);
      toast({ title: "Failed to submit feedback", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
    setRating(0);
    setSelectedOptions([]);
    setTextFeedback("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg">Quick Feedback</DialogTitle>
          <DialogDescription className="text-sm">
            Help us improve! How was your experience?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Star Rating */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-sm text-muted-foreground">Rate your experience</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="p-1 transition-transform hover:scale-110"
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  onClick={() => setRating(star)}
                >
                  <Star
                    className={`h-7 w-7 transition-colors ${
                      star <= (hoveredRating || rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted-foreground/40"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Quick Options */}
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground block text-center">
              Quick feedback (select any)
            </span>
            <div className="flex flex-wrap justify-center gap-2">
              {QUICK_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggleOption(opt.id)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                    selectedOptions.includes(opt.id)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card hover:bg-muted"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Text Feedback */}
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground block text-center">
              Anything else? (optional)
            </span>
            <Textarea
              value={textFeedback}
              onChange={(e) => setTextFeedback(e.target.value)}
              placeholder="Tell us more..."
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="ghost" onClick={handleSkip} className="flex-1">
            Skip
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="flex-1">
            {isSubmitting ? "Sending..." : "Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
